import React from 'react';
import SiteFooter from '../components/footer/SiteFooter';
import SiteHeader from '../components/navigation/SiteHeader';
import SkipLink from '../components/ui/SkipLink';
import ProjectVisual from '../components/work/ProjectVisual';
import cvProjects from '../data/cvProjects';
import styles from './CvPage.module.css';

const CHAPTERS = [
  {
    id: 'research',
    title: 'Research systems',
    description:
      'Small, controlled studies of temporal evidence, viewpoint shift, geometry, and multimodal state.',
  },
  {
    id: 'applied',
    title: 'Applied perception',
    description:
      'Computer vision inside larger production and autonomy systems, where deployment and evaluation constraints matter as much as the model.',
  },
];

function MetricStrip({ metrics }) {
  return (
    <dl className={styles.metrics} aria-label="Selected project metrics">
      {metrics.map((metric) => (
        <div className={styles.metric} key={`${metric.value}-${metric.label}`}>
          <dt>{metric.label}</dt>
          <dd>{metric.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function CvProject({ project }) {
  return (
    <article className={styles.project} id={project.id}>
      <div className={styles.projectHeader}>
        <div>
          <p className={styles.projectLabel}>{project.label}</p>
          <h3>{project.title}</h3>
        </div>
        <p className={styles.period}>{project.period}</p>
      </div>

      <p className={styles.description}>{project.description}</p>
      <MetricStrip metrics={project.metrics} />
      <ProjectVisual media={project.media} />
      <p className={styles.tech}>{project.tech}</p>
      <p className={styles.note}>{project.note}</p>
    </article>
  );
}

export default function CvPage() {
  return (
    <>
      <SkipLink />
      <SiteHeader />
      <main className={styles.main} id="main-content">
        {CHAPTERS.map((chapter) => {
          const projects = cvProjects.filter((project) => project.chapter === chapter.id);
          return (
            <section
              className={styles.chapter}
              key={chapter.id}
              aria-labelledby={`${chapter.id}-title`}
            >
              <div className="section-heading">
                <h2 id={`${chapter.id}-title`}>{chapter.title}</h2>
                <p>{chapter.description}</p>
              </div>
              <div className={styles.projects}>
                {projects.map((project) => (
                  <CvProject project={project} key={project.id} />
                ))}
              </div>
            </section>
          );
        })}

        <section className={styles.disclosure} aria-labelledby="evaluation-title">
          <h2 id="evaluation-title">How to read these results</h2>
          <p>
            Metrics keep their original evaluation context. Research prototypes are labeled as
            prototypes, development-validation results are not presented as official test scores,
            and illustrative website instruments are not presented as measured telemetry.
          </p>
          <p>
            RallyVision uses OpenTTGames under CC BY-NC-SA 4.0. ViewShift uses Assembly101 under
            CC BY-NC 4.0. The displayed media is limited to attributed noncommercial research
            artifacts and measured figures.
          </p>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
